/**
 * useConvexSync - React hook that bridges Convex reactive queries with Zustand stores
 *
 * Subscribes to Convex reactive queries for room data, queue, playback state,
 * processing jobs, and emoji events. Automatically syncs updates into the
 * corresponding Zustand stores, replacing the old Supabase realtime subscriptions.
 *
 * Usage in pages:
 *   const { isLoading, roomNotFound } = useConvexSync(roomCode)
 */
import { useQuery } from 'convex/react'
import { useEffect, useRef } from 'react'
import { api, coerceSong } from '../lib/convexHelpers'

import useRoomStore from '../stores/roomStore'
import useQueueStore from '../stores/queueStore'
import usePlaybackStore from '../stores/playbackStore'
import useProcessingStore from '../stores/processingStore'
import useEmojiStore from '../stores/emojiStore'
import useSearchStore from '../stores/searchStore'

export default function useConvexSync(roomCode) {
  // Subscribe to room data (includes queue, playback state, current song)
  const roomData = useQuery(
    api.rooms.getByCode,
    roomCode ? { code: roomCode } : "skip"
  )

  // Subscribe to processing jobs for this room
  const roomId = roomData?._id ?? roomData?.id
  const processingJobs = useQuery(
    api.processingJobs.list,
    roomId ? { roomId } : "skip"
  )

  // Subscribe to emoji events (last 60 seconds)
  const emojiSinceRef = useRef(Date.now() - 60_000)
  const emojiEvents = useQuery(
    api.emoji.recent,
    roomCode ? { roomCode, since: emojiSinceRef.current } : "skip"
  )

  // Track previous processing job statuses for transition detection
  const prevJobStatusesRef = useRef(new Map())
  const hasInitialDataRef = useRef(false)

  // Sync room + queue + playback data into stores
  useEffect(() => {
    if (roomData === undefined) return // Still loading

    if (roomData === null) {
      // Room not found
      useRoomStore.setState({
        isConnected: false,
        connectionStatus: 'error',
      })
      return
    }

    // Update room store
    useRoomStore.setState({
      room: roomData,
      roomCode: roomData.code,
      roomId,
      isConnected: true,
      connectionStatus: 'connected',
    })

    useQueueStore.getState().setQueue([...(roomData.queue || [])])

    // Update playback (unless skip is in progress to avoid overwriting optimistic state)
    const playbackStore = usePlaybackStore.getState()
    playbackStore.setAuthoritativePlayback({
      isPlaying: roomData.isPlaying ?? false,
      posSeconds: roomData.posSeconds ?? 0,
      anchorPosSec: roomData.anchorPosSec ?? roomData.posSeconds ?? 0,
      anchorServerMs: roomData.anchorServerMs ?? 0,
      revision: roomData.revision ?? 0,
      lastWriterClientId: roomData.lastWriterClientId ?? null,
      lastWriterSeq: roomData.lastWriterSeq ?? null,
    })

    if (!playbackStore.skipInProgress) {
      const newCursor = roomData.cursor ?? 0
      const newSong = coerceSong(roomData.currentSong)

      playbackStore.setCursor(newCursor)
      playbackStore.setPitchShift(roomData.pitchShift ?? 0)
      playbackStore.setCurrentSong(newSong)
      playbackStore.setPlaybackRevision(roomData.revision ?? 0)
    }

    hasInitialDataRef.current = true
  }, [roomData, roomId])

  // Sync processing jobs into store with status transition detection
  useEffect(() => {
    if (!processingJobs) return

    const prevStatuses = prevJobStatusesRef.current
    const jobsMap = new Map()

    for (const job of processingJobs) {
      jobsMap.set(job.jobId, job)

      // Detect status transitions (only after initial load)
      if (hasInitialDataRef.current) {
        const prevStatus = prevStatuses.get(job.jobId)
        if (prevStatus && prevStatus !== job.status) {
          if (job.status === 'completed') {
            // Dynamically import toast to avoid circular deps
            import('react-hot-toast').then(({ default: toast }) => {
              toast.success('Song processed successfully!')
            })
            // Reload songs list so newly processed song appears in search
            useSearchStore.getState().loadSongs()
          } else if (job.status === 'failed') {
            import('react-hot-toast').then(({ default: toast }) => {
              toast.error('Song processing failed')
            })
          }
        }
      }
    }

    // Update previous statuses for next comparison
    prevJobStatusesRef.current = new Map(
      processingJobs.map(j => [j.jobId, j.status])
    )

    useProcessingStore.setState({
      processingJobs: jobsMap,
      jobsLoaded: true,
      jobsLoading: false,
    })
  }, [processingJobs])

  // Sync emoji events into store
  const seenEmojiIdsRef = useRef(new Set())
  useEffect(() => {
    if (!emojiEvents) return

    for (const event of emojiEvents) {
      if (!seenEmojiIdsRef.current.has(event.eventId)) {
        seenEmojiIdsRef.current.add(event.eventId)
        useEmojiStore.getState().addEmojiEvent({
          id: event.eventId,
          emoji: event.emoji,
          sentAt: event.sentAt,
        })
      }
    }
  }, [emojiEvents])

  return {
    isLoading: roomCode && roomData === undefined,
    roomNotFound: roomCode && roomData === null,
    hasInitialData: hasInitialDataRef.current,
  }
}
